import { useState, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase';

export const useMessages = (user, updateActivity) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const subscriptionRef = useRef(null);

  useEffect(() => {
    if (!user?.id) return;

    const fetchMessages = async () => {
      try {
        setLoading(true);
        console.log('Fetching messages from Supabase...');

        // 清理測試數據
        await supabase
          .from('messages')
          .delete()
          .eq('nickname', 'Test User');

        const { data, error } = await supabase
          .from('messages')
          .select('*')
          .order('created_at', { ascending: true })
          .limit(100);

        if (error) {
          console.error('Error fetching messages:', error);
          setMessages([]);
        } else {
          console.log('Messages fetched successfully:', data?.length || 0);
          
          // 過濾掉測試訊息
          const filteredMessages = (data || []).filter(
            msg => msg.nickname !== 'Test User' && msg.content !== 'Test message'
          );
          
          setMessages(filteredMessages);
        }
      } catch (error) {
        console.error('Error fetching messages:', error);
        setMessages([]);
      } finally {
        setLoading(false);
      }
    };

    const setupSubscription = () => {
      try {
        console.log('Setting up realtime subscription for messages...');
        
        if (subscriptionRef.current) {
          subscriptionRef.current.unsubscribe();
        }
        
        subscriptionRef.current = supabase
          .channel('messages-channel')
          .on('postgres_changes', 
            { event: 'INSERT', schema: 'public', table: 'messages' },
            (payload) => {
              console.log('New message received:', payload.new);
              
              // 過濾測試訊息
              if (payload.new.nickname === 'Test User' || payload.new.content === 'Test message') {
                console.log('Filtering out test message');
                return;
              }
              
              setMessages(prev => {
                const exists = prev.some(msg => msg.id === payload.new.id);
                if (exists) return prev;
                return [...prev, payload.new];
              });
            }
          )
          .on('postgres_changes', 
            { event: 'UPDATE', schema: 'public', table: 'messages' },
            (payload) => {
              console.log('Message updated:', payload.new);
              
              // 過濾測試訊息
              if (payload.new.nickname === 'Test User' || payload.new.content === 'Test message') {
                console.log('Filtering out test message update');
                return;
              }
              
              setMessages(prev => 
                prev.map(msg => msg.id === payload.new.id ? payload.new : msg)
              );
            }
          )
          .on('postgres_changes', 
            { event: 'DELETE', schema: 'public', table: 'messages' },
            (payload) => {
              console.log('Message deleted:', payload.old);
              setMessages(prev => 
                prev.filter(msg => msg.id !== payload.old.id)
              );
            }
          )
          .subscribe((status) => {
            console.log('Messages subscription status:', status);
          });
        
        console.log('Messages subscription set up successfully');
      } catch (error) {
        console.error('Messages subscription setup error:', error);
      }
    };

    fetchMessages();
    setupSubscription();

    return () => {
      console.log('Cleaning up messages subscription...');
      if (subscriptionRef.current) {
        try {
          subscriptionRef.current.unsubscribe();
        } catch (error) {
          console.warn('Messages unsubscribe error:', error);
        }
      }
    };
  }, [user?.id]);

  const sendMessage = async (content) => {
    if (!user?.id || !user?.nickname || !content.trim()) {
      console.error('Invalid message data:', { 
        userId: user?.id, 
        nickname: user?.nickname, 
        content 
      });
      return false;
    }

    // 防止發送測試訊息
    if (user.nickname === 'Test User' || content.trim() === 'Test message') {
      console.log('Prevented sending test message');
      return false;
    }

    try {
      // 更新用戶活動時間
      if (updateActivity) {
        updateActivity();
      }

      const newMessage = {
        id: `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        user_id: user.id,
        nickname: user.nickname,
        content: content.trim(),
        created_at: new Date().toISOString(),
        deleted: false
      };

      console.log('Sending message to database:', newMessage);
      
      const { data, error } = await supabase
        .from('messages')
        .insert([newMessage])
        .select();

      if (error) {
        console.error('Database insert failed:', error);
        console.error('Error details:', error.message, error.details, error.hint);
        return false;
      }
      
      console.log('Message saved to database successfully:', data);
      return true;
    } catch (error) {
      console.error('Error sending message:', error);
      return false;
    }
  };

  const deleteMessage = async (messageId) => {
    if (!user?.id || !messageId) {
      console.error('Invalid delete data:', { 
        userId: user?.id, 
        messageId 
      });
      return false;
    }

    try {
      // 更新用戶活動時間
      if (updateActivity) {
        updateActivity();
      }

      console.log('Deleting message:', messageId);
      
      const { data, error } = await supabase
        .from('messages')
        .update({ deleted: true })
        .eq('id', messageId)
        .eq('user_id', user.id)
        .select();

      if (error) {
        console.error('Database update failed:', error);
        console.error('Error details:', error.message, error.details, error.hint);
        return false;
      }
      
      console.log('Message deleted from database successfully:', data);
      return true;
    } catch (error) {
      console.error('Error deleting message:', error);
      return false;
    }
  };

  return { messages, loading, sendMessage, deleteMessage };
};